import React, { useEffect, useState } from "react";
import { accessApi } from "../../services/accessApi";

const EMPTY_FORM = {
  company_name: "Modular Objects",
  website: "https://modularobjects.com",
  linkedin_url: "",
  instagram_url: "",
  facebook_url: "",
  youtube_url: "",
  pinterest_url: "",
  twitter_url: "",
  whatsapp_url: "",
  telegram_url: "",
  vcard_url: "",
  logo_url: "",
  profile_image_url: "",
  accent_color: "#d6c176",
  tagline: "",
  company_address: "",
  city: "",
  country: "",
};

export default function ITSignatureBrandingDefaultsPage() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      setMessage("");

      const res = await accessApi.getMailSignatureBrandingDefaults();
      const data = res?.data?.data || res?.data?.item || null;

      if (data) {
        setForm({
          ...EMPTY_FORM,
          ...data,
        });
      } else {
        setForm(EMPTY_FORM);
      }
    } catch (err) {
      console.error("Failed to load signature branding defaults:", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          "Failed to load branding defaults."
      );
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setMessage("");

      await accessApi.updateMailSignatureBrandingDefaults(form);

      setMessage("Branding defaults saved successfully.");
      await load();
    } catch (err) {
      console.error("Failed to save signature branding defaults:", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message ||
          "Failed to save branding defaults."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-black dark:text-white">Loading branding defaults...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-black dark:text-white">
          Signature Branding Defaults
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Central corporate defaults for logos, website and social links. User-specific values override these only when needed.
        </p>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900 px-4 py-3">
          {error}
        </div>
      ) : null}

      {message ? (
        <div className="rounded-xl border border-green-200 bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900 px-4 py-3">
          {message}
        </div>
      ) : null}

      <div className="rounded-2xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field
            label="Company name"
            value={form.company_name}
            onChange={(v) => updateField("company_name", v)}
          />

          <Field
            label="Website"
            value={form.website}
            onChange={(v) => updateField("website", v)}
          />

          <Field
            label="LinkedIn URL"
            value={form.linkedin_url}
            onChange={(v) => updateField("linkedin_url", v)}
          />

          <Field
            label="Instagram URL"
            value={form.instagram_url}
            onChange={(v) => updateField("instagram_url", v)}
          />

          <Field
            label="Facebook URL"
            value={form.facebook_url}
            onChange={(v) => updateField("facebook_url", v)}
          />

          <Field
            label="YouTube URL"
            value={form.youtube_url}
            onChange={(v) => updateField("youtube_url", v)}
          />

          <Field
            label="Pinterest URL"
            value={form.pinterest_url}
            onChange={(v) => updateField("pinterest_url", v)}
          />

          <Field
            label="Twitter / X URL"
            value={form.twitter_url}
            onChange={(v) => updateField("twitter_url", v)}
          />

          <Field
            label="WhatsApp URL"
            value={form.whatsapp_url}
            onChange={(v) => updateField("whatsapp_url", v)}
          />

          <Field
            label="Telegram URL"
            value={form.telegram_url}
            onChange={(v) => updateField("telegram_url", v)}
          />

          <Field
            label="vCard URL"
            value={form.vcard_url}
            onChange={(v) => updateField("vcard_url", v)}
          />

          <Field
            label="Logo URL"
            value={form.logo_url}
            onChange={(v) => updateField("logo_url", v)}
          />

          <Field
            label="Profile image URL"
            value={form.profile_image_url}
            onChange={(v) => updateField("profile_image_url", v)}
          />

          <Field
            label="Accent color"
            value={form.accent_color}
            onChange={(v) => updateField("accent_color", v)}
            placeholder="#d6c176"
          />

          <Field
            label="City"
            value={form.city}
            onChange={(v) => updateField("city", v)}
          />

          <Field
            label="Country"
            value={form.country}
            onChange={(v) => updateField("country", v)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4">
          <TextAreaField
            label="Tagline"
            value={form.tagline}
            onChange={(v) => updateField("tagline", v)}
            rows={3}
          />

          <TextAreaField
            label="Company address"
            value={form.company_address}
            onChange={(v) => updateField("company_address", v)}
            rows={3}
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            onClick={load}
            disabled={saving}
            className="px-4 py-2 rounded-xl border border-gray-300 dark:border-neutral-700"
          >
            Reset
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-yellow-500 text-black font-medium disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Defaults"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder = "" }) {
  return (
    <div>
      <div className="text-sm mb-1 text-black dark:text-white">{label}</div>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
      />
    </div>
  );
}

function TextAreaField({ label, value, onChange, rows = 4 }) {
  return (
    <div>
      <div className="text-sm mb-1 text-black dark:text-white">{label}</div>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="w-full rounded-xl border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-3 py-2 text-black dark:text-white"
      />
    </div>
  );
}